import { PlusOutlined } from "@ant-design/icons";
import { Button, FormInstance, message, Table } from "antd";
import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router";
import { NavLink } from "react-router-dom";
import { IField } from "../components/AdvancedSearchModal";
import Card from "../components/Card";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import {
  APIError,
  Trustee,
  TrusteeCompanySubscription,
} from "../helpers/shared-interfaces";
import { handleValidationErrors, request } from "../helpers/utilities";
import {
  TRUSTEE_VAN_LABEL_BY_FUND_CODE,
  trusteeVanLabelFor,
} from "../helpers/constants";
import { CREATE_TRUSTEE, GET_TRUSTEES } from "../gql/trustees";
import ModalCreator from "../components/ModalCreator";
import { PageToolbar } from "../components/PageToolbar";
import TrusteeForm, {
  TRUSTEE_FORM_FIELD_NAMES,
} from "../components/TrusteeForm";
import { AxiosResponse } from "axios";
import * as Sentry from "@sentry/browser";
import { generateDisplayErrorMessage } from "../helpers/utilities";


interface State {
  isFiltering: boolean;
  trustees: Trustee[];
  page: number;
  trusteesCount: number;
  filteredTrustees: Trustee[];
  filterBy: string;
  isLoading: boolean;
  showDialog: boolean;
  toggleUserImport: boolean;
  showNewTrusteeModal: boolean;
  isCreatingTrustee: boolean;
}

export function UserCountRender(): JSX.Element {
  return <span>{0}</span>;
}

const TRUSTEE_VAN_COLUMN_LABELS = Object.values(TRUSTEE_VAN_LABEL_BY_FUND_CODE);

export const baseColumns = [
  {
    title: "Trustee Name",
    dataIndex: "name",
    key: "Trustee Name",
    render(text: string, record: Trustee): JSX.Element {
      return (
        <NavLink
          to={`/trustees/${record.id}`}
          data-cy="test-trustee-details-link"
        >
          {text}
        </NavLink>
      );
    },
  },
  {
    title: "Petra ID",
    key: "Petra ID",
    render(_: unknown, record: Trustee): string {
      return record.company?.petraId || "N/A";
    },
  },
  ...TRUSTEE_VAN_COLUMN_LABELS.map((label: string) => ({
    title: label,
    key: label,
    render(_: unknown, record: Trustee): string {
      return (
        (record.trusteeCompanySubscriptions || []).find(
          (cs: TrusteeCompanySubscription): boolean =>
            trusteeVanLabelFor(cs) === label,
        )?.companyVan?.van || "N/A"
      );
    },
  })),
];

export function TrusteeTableFactory(
  data: Trustee[],
  isLoading: boolean,
  count: number,
  page: number,
  nextPage: (page: number) => void,
): JSX.Element {
  const tempData: Trustee[] = data.map((trustee: Trustee): Trustee => {
    return { ...trustee, key: trustee.id };
  });
  return (
    <Table
      loading={isLoading}
      bordered={true}
      columns={baseColumns}
      dataSource={tempData}
      pagination={{
        current: page,
        defaultPageSize: 25,
        total: count,
        onChange: nextPage,
      }}
    />
  );
}

export default class Trustees extends React.Component<
  RouteComponentProps,
  State
> {
  reference: RefObject<FormInstance>;

  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      showDialog: false,
      filterBy: "",
      isLoading: true,
      toggleUserImport: false,
      trustees: [],
      page: 1,
      trusteesCount: 0,
      filteredTrustees: [],
      isFiltering: false,
      showNewTrusteeModal: false,
      isCreatingTrustee: false,
    };
    this.reference = React.createRef<FormInstance>();
    this.getAllTrustees = this.getAllTrustees.bind(this);
    this.toggleCreateTrusteeModal = this.toggleCreateTrusteeModal.bind(this);
    this.filterTrustees = this.filterTrustees.bind(this);
    this.nextPage = this.nextPage.bind(this);
  }

  async filterTrustees(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    e.persist();
    const filterBy: string = e.target.value;

    this.setState({ filterBy, isFiltering: true });

    if (filterBy.length === 0) {
      this.setState({ isFiltering: false });
      return;
    }

    const needle: string = filterBy.toLowerCase();
    const filtered: Trustee[] = this.state.trustees.filter(
      (trustee: Trustee): boolean => {
        const vanValues: string[] = (
          trustee.trusteeCompanySubscriptions || []
        )
          .map(
            (cs: TrusteeCompanySubscription): string | undefined =>
              cs.companyVan?.van,
          )
          .filter((v): v is string => !!v);
        return [trustee.name, trustee?.company?.petraId, ...vanValues].some(
          (field: string | null | undefined): boolean =>
            !!field && field.toLowerCase().includes(needle),
        );
      },
    );

    this.setState({ filteredTrustees: filtered });
  }

  getAllTrustees(): void {
    this.setState({ isLoading: true });
    request({
      query: GET_TRUSTEES,
    })
      .then(({ data: { data } }: any): void => {
        this.setState({
          trustees: data.trustees,
          trusteesCount: data.trustees.length,
        });
      })
      .catch((error) => {
        message.error("Failed to load trustees");
        Sentry.captureException(error);
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  }

  componentDidMount(): void {
    this.setState({ isLoading: true }, (): void => {
      this.getAllTrustees();
    });
  }

  sortFieldsByNameAsc = (fields: IField[]) => {
    return fields.sort(function (a, b) {
      const textA = a.name.toUpperCase();
      const textB = b.name.toUpperCase();
      return textA < textB ? -1 : textA > textB ? 1 : 0;
    });
  };

  nextPage(page: number) {
    this.setState({ isLoading: true, page }, (): void => {
      this.getAllTrustees();
    });
  }

  toggleCreateTrusteeModal(): void {
    this.setState((prevState) => ({
      showNewTrusteeModal: !prevState.showNewTrusteeModal,
    }));
  }

  createTrustee = async (): Promise<void> => {
    interface Response {
      data?: {
        createTrustee?: {
          trustee?: Trustee;
          errors?: APIError[];
        };
      };
      errors?: Record<string, unknown>[];
    }

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields(
        TRUSTEE_FORM_FIELD_NAMES,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isCreatingTrustee: true });

    try {
      const response: AxiosResponse<Response> = await request({
        query: CREATE_TRUSTEE,
        variables: data,
      });

      if (
        response &&
        response.data &&
        response.data.data &&
        response.data.data.createTrustee &&
        response.data.data.createTrustee.trustee
      ) {
        message.success("Trustee created successfully");
        this.getAllTrustees?.();
      } else {
        message.error("Failed to create trustee");
      }
    } catch (error) {
      message.error(`Failed to create trustee - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isCreatingTrustee: false });
      this.toggleCreateTrusteeModal?.();
    }
  };

  render(): JSX.Element | null {
    const {
      trustees,
      isFiltering,
      filteredTrustees,
      isLoading,
      trusteesCount,
      page,
      showNewTrusteeModal,
      isCreatingTrustee,
    } = this.state;
    return (
      <>
        {showNewTrusteeModal &&
          ModalCreator({
            title: "Add Trustee",
            okType: "primary",
            okText: "Create",
            showModal: showNewTrusteeModal,
            onOkay: this.createTrustee,
            toggleModal: this.toggleCreateTrusteeModal,
            inscription: (
              <TrusteeForm
                reference={this.reference}
                isLoading={isCreatingTrustee}
              />
            ),
          })}
        <Card>
          <div className="deparments-table-container">
            <PageToolbar modelName="trustees"
              searchPlaceholder="Filter by ..."
              searchValue={this.state.filterBy}
              onSearchChange={this.filterTrustees}
              primaryAction={
                <EntitledSection entitlement={entitlements.Trustees.create}>
                  <Button
                    type="primary"
                    onClick={this.toggleCreateTrusteeModal}
                    data-cy="test-new-user-button"
                  >
                    <PlusOutlined /> Add Trustee
                  </Button>
                </EntitledSection>
              }
              totalCount={trustees.length}
              currentDataCount={
                isFiltering ? filteredTrustees.length : trustees.length
              }
              page={1}
              pageSize={trustees.length}
            />
            {TrusteeTableFactory(
              isFiltering ? filteredTrustees : trustees,
              isLoading,
              trusteesCount,
              page,
              this.nextPage,
            )}
          </div>
        </Card>
      </>
    );
  }
}
